import { makeEntityScaffold } from '@classpass/saga-tools';
import { transformKeysToSnakeCase, transformClientResponse } from 'helpers/core';
import _defaultsDeep from 'lodash/defaultsDeep';
import _isEqual from 'lodash/isEqual';
import _isPlainObject from 'lodash/isPlainObject';
import _mapValues from 'lodash/mapValues';
import _toNumber from 'lodash/toNumber';

import URLS from 'constants/urls';

const EMPTY_EXCEPTION_PERIOD = { startTime: '', endTime: '' };
const EMPTY_PAYOUT_RATE = { defaultFloor: null, exceptionFloor: null };
const EMPTY_CONTROLS = {
  payoutRates: {
    premium: EMPTY_PAYOUT_RATE,
    network: EMPTY_PAYOUT_RATE,
  },
  exceptionPeriods: {
    monday: [EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD],
    tuesday: [EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD],
    wednesday: [EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD],
    thursday: [EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD],
    friday: [EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD],
    saturday: [EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD],
    sunday: [EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD, EMPTY_EXCEPTION_PERIOD],
  },
};

const mapValuesDeep = (value, callback) => {
  if (value === null) return value;

  return _isPlainObject(value) ? _mapValues(value, v => mapValuesDeep(v, callback)) : callback(value);
};

const getEndpoints = venueId => {
  return {
    timeExceptionsEndpoint: URLS.venue.pricingControls.timeExceptions.replace('{venueId}', venueId),
    classFloorsEndpoint: URLS.venue.pricingControls.classFloors.replace('{venueId}', venueId),
  };
};

const scrubTimeExceptions = exceptionPeriods => {
  return transformKeysToSnakeCase(
    Object.keys(exceptionPeriods).reduce((scrubbed, day) => {
      const exceptions = exceptionPeriods[day].filter(({ startTime, endTime }) => startTime.trim() && endTime.trim());
      return exceptions.length > 0 ? { ...scrubbed, [day]: exceptions } : scrubbed;
    }, {})
  );
};

const scrubPayoutRates = payoutRates => {
  return transformKeysToSnakeCase(mapValuesDeep(payoutRates, rate => _toNumber(rate)));
};

const scrubClassFloors = classes => {
  const classFloorsById = classes.reduce((classesById, { classId, payoutRates }) => {
    if (!classId) {
      return classesById;
    }

    return Object.assign(classesById, {
      [classId]: {
        payoutRates: _defaultsDeep(payoutRates, EMPTY_CONTROLS.payoutRates),
      },
    });
  }, {});

  return transformKeysToSnakeCase(mapValuesDeep(classFloorsById, rate => _toNumber(rate)));
};

const get = (httpClient, venueId) => {
  const { timeExceptionsEndpoint, classFloorsEndpoint } = getEndpoints(venueId);

  return Promise.all([httpClient.get(timeExceptionsEndpoint), httpClient.get(classFloorsEndpoint)]).then(
    ([timeExceptionsRes, classFloorsRes]) => {
      const timeExceptions = _defaultsDeep(transformClientResponse(timeExceptionsRes), EMPTY_CONTROLS);
      const classFloorsData = transformClientResponse(classFloorsRes);
      const classFloors = Object.keys(classFloorsData).map(classId => {
        const data = classFloorsData[classId];
        return Object.assign(
          data,
          { classId },
          { payoutRates: _defaultsDeep(data.payoutRates, EMPTY_CONTROLS.payoutRates) }
        );
      });
      return Object.assign(timeExceptions, { classes: classFloors });
    }
  );
};

const post = (httpClient, venueId, controls, initialControls) => {
  const { exceptionPeriods, payoutRates, classes } = controls;
  const { timeExceptionsEndpoint, classFloorsEndpoint } = getEndpoints(venueId);

  const hasTimeExceptionsChanges = !_isEqual(exceptionPeriods, initialControls.exceptionPeriods);
  const hasPayoutRateChanges = !_isEqual(payoutRates, initialControls.payoutRates);
  const hasClassFloorChanges = !_isEqual(classes, initialControls.classes);

  const scrubbedTimeExceptions = scrubTimeExceptions(exceptionPeriods);
  const scrubbedPayoutRates = scrubPayoutRates(payoutRates);
  const scrubbedClassFloors = scrubClassFloors(classes);

  const saveStudioControls =
    !hasTimeExceptionsChanges && !hasPayoutRateChanges
      ? Promise.resolve()
      : httpClient.post(timeExceptionsEndpoint, {
          exception_periods: scrubbedTimeExceptions,
          payout_rates: scrubbedPayoutRates,
        });
  const saveClassControls = !hasClassFloorChanges
    ? Promise.resolve()
    : httpClient.post(classFloorsEndpoint, scrubbedClassFloors);

  return Promise.all([saveStudioControls, saveClassControls]).then(() => controls);
};

const handler = ({ httpClient }, venueId, controls, initialControls) => {
  if (controls) {
    return post(httpClient, venueId, controls, initialControls);
  }

  return get(httpClient, venueId);
};

export default makeEntityScaffold('SAGA.API.DYNAMIC_PRICING.CONTROLS', handler, {
  defaultKey: (client, venueId) => venueId,
});
